import { useOptionalAuth } from './authContext';

/**
 * Whether the signed-in tenant may WRITE (slice 11b). Sourced from the `/api/me`
 * user's `writable` field (seam S1).
 *
 * A SUSPENDED (unpaid) tenant reads `false`: they keep full READ access — map,
 * vehicles, trips, reports, alerts — but every write is refused server-side with
 * a 402 `tenant_suspended`, and the UI hides the write affordances (absence over
 * disabled) and shows a persistent pay-to-restore banner. Active / past-due
 * tenants read `true`.
 *
 * 🔴 ABSENT MEANS TRUE. An older API body carries no `writable` key; treating a
 * missing field as writable keeps existing tenants working while the backend half
 * of the seam lands. Only an explicit `writable === false` gates. This is the ONE
 * place that rule lives — callers ask this hook, never read `state.user.writable`.
 *
 * 🔴 A READ-ONLY IMPERSONATION SESSION IS NEVER WRITABLE (slice 17c). A staff
 * member viewing a customer's fleet must see no write affordance at all — the
 * server refuses every non-GET with `read_only_session`, so a visible edit/command
 * control would only ever fail. `readOnly === true` gates here regardless of the
 * tenant's own `writable` (an active tenant's body carries `writable: true`), so
 * this hook — not the suspension flag alone — is what hides writes during
 * impersonation. Same "absence over disabled" rule: the control is not rendered.
 *
 * Outside an authenticated tenant session (loading / staff / unauthenticated),
 * and when no `<AuthProvider>` is mounted at all (an isolation-tested surface),
 * it returns `true`: there is no fleet to gate, and the controls it guards are
 * not mounted in those states anyway.
 */
export function useTenantWritable(): boolean {
  const ctx = useOptionalAuth();
  if (!ctx || ctx.state.status !== 'authenticated') return true;
  const { user } = ctx.state;
  if (user.readOnly === true) return false;
  return user.writable !== false;
}
